import type {
  GetMbcBriefResponse,
  MbcBrief,
  MbcBriefQuality,
} from "@nlc/shared-types";

import { useCallback, useEffect, useState } from "react";

import { MBC_MASTRA_API_URL } from "../config/mbc-chat.config";
import { patchBrief } from "../lib/brief-api";

export type BriefMemory = MbcBrief;

const PENDING_POLL_MS = 5000;
// The agent persists the brief near the end of the stream and the judge only
// starts then, so a fetch right on turn-complete can still read the old score.
const SETTLE_MS = 2000;

type UseBriefMemoryArgs = {
  /** Bump this after each completed turn to refetch the persisted form. */
  refreshSignal: number;
  resourceId: string;
  threadId: string;
};

function briefUrl(threadId: string, resourceId: string): string {
  const params = new URLSearchParams({ resourceId });

  return `${MBC_MASTRA_API_URL}/mbc/brief/${encodeURIComponent(threadId)}?${params.toString()}`;
}

/**
 * Reads the agent's working memory (the brief form Mastra persists in MongoDB)
 * through the AI module's `/mbc/brief/:threadId` route, which reads the same
 * underlying document as Mastra's generic memory API but also returns
 * the deterministic quality card (no JSON string to parse — already an object).
 *
 * Uses a plain fetch (not @mastra/client-js) to keep the browser bundle light
 * and avoid that SDK's Node-only transitive deps.
 */
export function useBriefMemory({
  refreshSignal,
  resourceId,
  threadId,
}: UseBriefMemoryArgs): {
  error: unknown;
  /** A turn just finished and its score is not in yet. */
  isSettling: boolean;
  memory: BriefMemory | null;
  quality: MbcBriefQuality | null;
  recheckFiles: () => Promise<void>;
  refetch: () => Promise<void>;
} {
  const [memory, setMemory] = useState<BriefMemory | null>(null);
  const [quality, setQuality] = useState<MbcBriefQuality | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isSettling, setIsSettling] = useState(false);

  const fetchMemory = useCallback(async () => {
    if (!threadId) return;
    try {
      const response = await fetch(briefUrl(threadId, resourceId));

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const body = (await response.json()) as GetMbcBriefResponse;
      const brief = body.brief ?? null;

      setMemory(brief);
      setQuality(body.quality ?? null);
      setError(null);
    } catch (error_) {
      setError(error_);
    }
  }, [resourceId, threadId]);

  // Files live outside the brief, so they are re-judged by an empty PATCH on
  // the route the review page uses. The brief is read first so the files show
  // without waiting for the judge, then read again for the grade.
  const recheckFiles = useCallback(async () => {
    await fetchMemory();
    await patchBrief(threadId, resourceId, {}).catch(() => {});
    await fetchMemory();
  }, [fetchMemory, resourceId, threadId]);

  useEffect(() => {
    void fetchMemory();
    if (refreshSignal > 0) setIsSettling(true);
    const timer = setTimeout(() => {
      void fetchMemory().finally(() => setIsSettling(false));
    }, SETTLE_MS);

    return () => clearTimeout(timer);
  }, [fetchMemory, refreshSignal]);

  // Items still being judged in the background: keep polling until they settle.
  // Re-running on each new quality/error resets the single timer, never stacks.
  useEffect(() => {
    if (!quality?.items.some((item) => item.status === "pending")) return;

    const timer = setTimeout(() => void fetchMemory(), PENDING_POLL_MS);

    return () => clearTimeout(timer);
  }, [error, fetchMemory, quality]);

  return {
    error,
    isSettling,
    memory,
    quality,
    recheckFiles,
    refetch: fetchMemory,
  };
}
